import TwoEqualColumnLayout from '../../layouts/two-equal-column-layout/two-equal-column-layout'
import VerifyOrderForm from './verify-order-form/verify-order-form'
import VerifyOrderMobile from './verify-order-mobile/verify-order-mobile'
import VerifyOrderPdf from './verify-order-pdf/verify-order-pdf'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetOrderByIdQuery } from 'store/orders/orders.api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useEffect, useState } from 'react'
import { useGetLoadconfirmationPresignedUrlMutation } from 'store/task-automation/task-automation.api'
import VerifyOrderPdfSkeletons from 'components/verify-order-pdf/verify-order-pdf-skeletons'
import { useGetCompanyByIdQuery } from 'store/company/company.api'
import { getLatestLoadConfirmationDocument } from 'utils/load-confirmation-documents'
import {
  useGetPreSignedUrlMutation,
  useListInvoicedocumentsQuery,
} from 'store/invoices/invoices.api'
import { Box, Dialog, DialogContent, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const VerifyOrder = () => {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const [pdfUrl, setPdfUrl] = useState<string>('')

  const { data: selectedOrder } = useGetOrderByIdQuery({
    orderId: Number(orderId) ?? skipToken,
  })
  
  const { data: selectedCompany } = useGetCompanyByIdQuery(
    selectedOrder?.companyId
      ? { id: selectedOrder.companyId }
      : skipToken
  )
  
  const [findPresignedUrl, { isError, isLoading }] =
    useGetLoadconfirmationPresignedUrlMutation()
  const [getPreSignedUrl] = useGetPreSignedUrlMutation()

  const { data: orderDocuments } = useListInvoicedocumentsQuery(
    orderId ? { orderId } : skipToken
  )

  const handleClose = useCallback(() => {
    navigate('/order')
  }, [navigate])

  const handlePdfUrlChange = useCallback(async () => {
    const latestLc = getLatestLoadConfirmationDocument(orderDocuments?.LC)

    try {
      if (latestLc?.s3Path) {
        const response = await getPreSignedUrl({
          s3URL: latestLc.s3Path,
          openInline: true,
        }).unwrap()
        setPdfUrl(response.preSignedURL)
        return
      }

      const presignedRes = await findPresignedUrl({
        orderId: orderId ?? '',
      }).unwrap()

      if (!isError) {
        setPdfUrl(presignedRes.presigned_url)
      }
    } catch (error) {
      console.error('Error while fetching presigned url', error)
    }
  }, [findPresignedUrl, getPreSignedUrl, isError, orderDocuments?.LC, orderId])

  useEffect(() => {
    if (orderId) {
      handlePdfUrlChange()
    }
  }, [handlePdfUrlChange, orderId])

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '95vw' },
          maxWidth: '95vw',
          height: { xs: '100%', sm: '95vh' },
          maxHeight: { xs: '100%', sm: '95vh' },
          m: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <IconButton
          aria-label="Close edit order"
          onClick={handleClose}
          disableRipple
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 20,
            color: 'error.main',
            width: 40,
            height: 40,
            bgcolor: 'common.white',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'grey.100' },
            '&:active': { backgroundColor: 'grey.200' },
            '& .MuiTouchRipple-root': { display: 'none' },
          }}
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <TwoEqualColumnLayout>
            {isLoading ? (
              <VerifyOrderPdfSkeletons />
            ) : (
              <VerifyOrderPdf pdfUrl={pdfUrl} />
            )}
            {!!selectedOrder && (
              <Box sx={{ height: '100%', minHeight: 0, pr: 5 }}>
                <VerifyOrderForm
                  order={selectedOrder}
                  company={selectedCompany}
                  onClose={handleClose}
                />
              </Box>
            )}
            <VerifyOrderMobile pdfUrl={pdfUrl} onClose={handleClose} />
          </TwoEqualColumnLayout>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default VerifyOrder
